import { EmptyState } from "@web/components/EmptyState.tsx";
import { useUiStore } from "@web/stores/ui.ts";
import { CopyDetailsButton } from "./CopyDetailsButton.tsx";
import { ErrorActionButton } from "./ErrorActionButton.tsx";
import { resolveError } from "./resolveError.ts";

/**
 * Inline load failure — sits where the grid/content would be.
 * Never looks like empty-because-no-data.
 */
export function QueryErrorState({
  error,
  onRetry,
  actionLabel,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  /** Shown in Copy details as Action: … */
  actionLabel?: string;
  className?: string;
}) {
  const debugMode = useUiStore((s) => s.debugMode);
  const resolved = resolveError(error);

  return (
    <EmptyState
      className={className}
      title={resolved.title}
      description={
        <>
          <span>{resolved.message}</span>
          {(debugMode || resolved.code === "UNKNOWN") && (
            <span className="mt-2 block font-mono text-xs text-faint">
              {resolved.code}
              {resolved.errorId ? ` · ${resolved.errorId}` : ""}
            </span>
          )}
          {!debugMode && resolved.errorId && resolved.code !== "UNKNOWN" && (
            <span className="mt-2 block font-mono text-xs text-faint">
              Ref {resolved.errorId}
            </span>
          )}
        </>
      }
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <ErrorActionButton action={resolved.definition.action} onRetry={onRetry} />
          {onRetry && resolved.definition.action.kind !== "retry" && (
            <ErrorActionButton
              action={{ label: "Retry", kind: "retry" }}
              onRetry={onRetry}
            />
          )}
          <CopyDetailsButton
            errorId={resolved.errorId}
            code={resolved.code}
            message={resolved.message}
            action={actionLabel}
          />
        </div>
      }
    />
  );
}
